import { I_BOOKMARK, I_DOWNLOAD } from '../../../types/images';
import ProfileImage from '../../ProfileImage';
import dayjs from 'dayjs';
import { CONDITION_TYPE } from '../../../lib/varialbe';
import {
    normalizeWorkoutRecord,
    WORKOUT_EXERCISE_MARKER,
    WorkoutExercise,
} from '../../../lib/workoutRecord';
import WorkoutRecordView, {
    WorkoutSummaryBlock,
    WorkoutClosingBlock,
} from '../WorkoutRecord/WorkoutRecordView';
import WorkoutExerciseCard from '../WorkoutRecord/WorkoutExerciseCard';
import { getFeedScheduleTitle, getFeedSource } from '../../../lib/feedSource';

const ModalFeedDetailBody = ({
    feed,
    contents,
    scheduleReport,
    receivers,
    postType,
    initialPost,
    setIsConditionModal,
    onClickImageAtDetailModal,
    onClickVideoAtDetailModal,
}: any) => {
    // 이미지+비디오 통합 미디어 리스트 (콘텐츠 순서 유지)
    const mediaList = contents ? contents.filter((item: any) => item.contentType === 'IMG' || item.contentType === 'VIDEO') : [];

    const handleMediaClick = (clickedItem: any) => {
        const clickedIndex = mediaList.findIndex((m: any) => m._id === clickedItem._id);
        if (onClickImageAtDetailModal) {
            onClickImageAtDetailModal(mediaList, clickedIndex >= 0 ? clickedIndex : 0);
        }
    };

    const handleDownload = (url: string) => {
        try {
            const link: any = document.createElement('a');
            link.href = url;
            link.setAttribute('download', true);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            alert('다운로드에 실패하였습니다.');
        }
    };

    const resolvedFeed = feed || { postType, scheduleReport };
    const isSchedule = postType === 'SCHEDULEREPORT';
    const feedSource = getFeedSource(resolvedFeed);
    const isNewOpportunity = feedSource === 'newOpportunity';
    const scheduleInfo = scheduleReport?.scheduleInfo;
    const condition = scheduleReport?.condition;
    const conditionText = condition
        ? CONDITION_TYPE.find((item: any) => item.code === condition.todayCondition)?.text
        : null;
    const conditionImg = condition
        ? CONDITION_TYPE.find((item: any) => item.code === condition.todayCondition)?.src
        : null;

    // ─ 운동 기록 (workoutRecord) ─
    // 백엔드가 post.scheduleReport.workoutRecord로 스냅샷을 그대로 내려준다(PT/그룹 동일).
    // 서버가 세트 요약을 안 주므로 클라에서 정규화 후 계산한다.
    const workoutRecord = normalizeWorkoutRecord(scheduleReport?.workoutRecord);
    // contents에 WORKOUT_EXERCISE 마커가 있으면 마커 위치에 운동 카드를 인터리브,
    // 없으면(현재 대부분) 하단에 번들 섹션으로 표시.
    const hasWorkoutMarkers =
        !!workoutRecord &&
        Array.isArray(contents) &&
        contents.some((c: any) => c?.contentType === WORKOUT_EXERCISE_MARKER);

    const exerciseByOrder: Record<number, WorkoutExercise> = {};
    if (workoutRecord) {
        workoutRecord.exercises.forEach((ex) => {
            exerciseByOrder[ex.order] = ex;
        });
    }
    // 마커가 참조하지 않은 운동은 마커 렌더 이후 이어붙여 누락 없이 표시한다.
    const markerOrders = hasWorkoutMarkers
        ? contents
              .filter((c: any) => c?.contentType === WORKOUT_EXERCISE_MARKER)
              .map((c: any) => Number(c.content))
        : [];
    const unreferencedExercises =
        workoutRecord && hasWorkoutMarkers
            ? workoutRecord.exercises.filter((ex) => !markerOrders.includes(ex.order))
            : [];

    // 운동별 영상/이미지 클릭 → 기존 이미지 뷰어 재사용(VIDEO 인라인 재생 지원)
    const handleWorkoutMediaClick = (items: any[], clickedIndex: number) => {
        if (onClickImageAtDetailModal) {
            onClickImageAtDetailModal(items, clickedIndex);
        }
    };

    return (
        <div className="w-full px-[24px] pt-[20px] pb-[8px]">
            {/* 수업일지: 수업 정보 카드 */}
            {isSchedule && scheduleInfo && (
                <div className="mb-[16px] bg-[#F0F4FA] border border-[#CBD5E1] rounded-[12px] p-[16px]">
                    <div className="flex items-center gap-[8px] mb-[10px]">
                        <span className="text-[13px] font-semibold text-[#1F2937]">
                            📋 {isNewOpportunity ? `${getFeedScheduleTitle(resolvedFeed)} 운동일지` : '수업 리포트'}
                        </span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-[24px] gap-y-[6px]">
                        <div className="flex items-center gap-[6px]">
                            <span className="text-[12px] text-[#6B7280] w-[52px]">회원</span>
                            <span className="text-[13px] font-medium text-[#374151]">
                                {receivers?.[0]?.receiverName}
                            </span>
                        </div>
                        <div className="flex items-center gap-[6px]">
                            <span className="text-[12px] text-[#6B7280] w-[52px]">수업 날짜</span>
                            <span className="text-[13px] text-[#374151]">
                                {dayjs(scheduleInfo.startTime).format('YYYY.MM.DD(ddd)')}
                            </span>
                        </div>
                        {isNewOpportunity ? (
                            <div className="flex items-center gap-[6px]">
                                <span className="text-[12px] text-[#6B7280] w-[52px]">유형</span>
                                <span className="text-[13px] font-medium text-[#6D28D9]">
                                    {getFeedScheduleTitle(resolvedFeed)}
                                </span>
                            </div>
                        ) : (
                            <div className="flex items-center gap-[6px]">
                                <span className="text-[12px] text-[#6B7280] w-[52px]">회차</span>
                                <span className="text-[13px] font-medium text-[#3B82F6]">
                                    {scheduleInfo.currentSession || '?'} / {scheduleInfo.totalSession || '?'}회
                                </span>
                            </div>
                        )}
                        <div className="flex items-center gap-[6px]">
                            <span className="text-[12px] text-[#6B7280] w-[52px]">수업 시간</span>
                            <span className="text-[13px] text-[#374151]">
                                {dayjs(scheduleInfo.startTime).format('A h:mm')} ~ {dayjs(scheduleInfo.endTime).format('A h:mm')}
                            </span>
                        </div>
                        {!isNewOpportunity && (
                            <>
                                <div className="flex items-center gap-[6px]">
                                    <span className="text-[12px] text-[#6B7280] w-[52px]">컨디션</span>
                                    <span className="flex items-center gap-[4px]">
                                        {conditionImg && <img src={conditionImg} className="w-[16px] h-[16px]" alt="" />}
                                        <span className="text-[13px] text-[#374151]">{conditionText || '기록 안함'}</span>
                                    </span>
                                </div>
                                <div className="flex items-center gap-[6px]">
                                    <span className="text-[12px] text-[#6B7280] w-[52px]">특이사항</span>
                                    <span className="text-[13px] text-[#374151]">
                                        {condition?.isSpecial ? '있음' : '없음'}
                                    </span>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* 답글 원본 */}
            {postType === 'REPLYBASIC' && initialPost && (
                <div className="w-full rounded-[12px] border border-[#E2E8F0] bg-[#F8FAFC] p-[16px] mb-[16px] relative">
                    <img
                        src={I_BOOKMARK}
                        className="absolute right-[12px] top-0 w-[24px] h-[32px]"
                        alt=""
                    />
                    <div className="flex items-center gap-[8px] mb-[10px]">
                        <ProfileImage
                            imageUrl={initialPost?.profileImageUrl}
                            type="VerySmall"
                            profileName={initialPost?.profileName}
                            profileColorType={initialPost?.profileColorType}
                            right={0}
                        />
                        <span className="text-[13px] font-semibold text-[#374151]">
                            {initialPost?.writerName}
                            <span className="text-[12px] font-normal text-[#9CA3AF] ml-[4px]">
                                {initialPost?.role === 'coach' ? '강사' : '회원'}
                            </span>
                        </span>
                        <span className="text-[11px] text-[#D1D5DB] ml-auto">
                            {dayjs(initialPost?.createdTime).format('MM.DD HH:mm')}
                        </span>
                    </div>
                    <p className="text-[13px] text-[#6B7280] leading-[20px] whitespace-pre-wrap pl-[32px]">
                        {initialPost?.contents?.[0]?.content}
                    </p>
                </div>
            )}

            {/* 운동 기록 요약 (마커 인터리브 모드: 본문 상단) */}
            {workoutRecord && hasWorkoutMarkers && workoutRecord.summaryText && (
                <div className="mb-[12px]">
                    <WorkoutSummaryBlock text={workoutRecord.summaryText} />
                </div>
            )}

            {/* 본문 콘텐츠 */}
            {contents &&
                contents.map((item: any, index: number) => (
                    <div className="mb-[12px]" key={index + 'feedDetailContent'}>
                        {/* 운동 카드 (WORKOUT_EXERCISE 마커 위치) */}
                        {item.contentType === WORKOUT_EXERCISE_MARKER &&
                            hasWorkoutMarkers &&
                            exerciseByOrder[Number(item.content)] && (
                                <WorkoutExerciseCard
                                    exercise={exerciseByOrder[Number(item.content)]}
                                    displayNumber={exerciseByOrder[Number(item.content)].order}
                                    onMediaClick={handleWorkoutMediaClick}
                                />
                            )}

                        {/* 텍스트 */}
                        {item.contentType === 'TEXT' && (
                            <p className="text-[14px] text-[#374151] leading-[22px] whitespace-pre-wrap">
                                {item.content}
                            </p>
                        )}

                        {item.contentType === 'IMG' && (
                            <img
                                src={item.content}
                                className="w-full rounded-[8px] cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={() => handleMediaClick(item)}
                                alt=""
                            />
                        )}

                        {item.contentType === 'VIDEO' && (
                            <div
                                onClick={() => handleMediaClick(item)}
                                className="relative cursor-pointer rounded-[8px] overflow-hidden group w-full"
                            >
                                <img
                                    src={item?.thumbnail}
                                    className="w-full h-auto opacity-80 group-hover:opacity-70 transition-opacity"
                                    style={{ aspectRatio: '16/9', objectFit: 'cover' }}
                                    alt=""
                                />
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-[56px] h-[56px] bg-black/50 rounded-full flex items-center justify-center backdrop-blur-sm">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                                            <polygon points="8,5 20,12 8,19" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* 파일 */}
                        {item.contentType === 'FILE' && (
                            <button
                                onClick={() => handleDownload(item.content)}
                                className="flex items-center gap-[12px] w-full px-[16px] py-[14px] bg-[#F8FAFC] border border-[#E2E8F0] rounded-[10px] hover:bg-[#EFF6FF] transition-colors cursor-pointer"
                                style={{ maxWidth: 500 }}
                            >
                                <div className="w-[36px] h-[36px] bg-[#EFF6FF] rounded-[8px] flex items-center justify-center flex-shrink-0">
                                    <img src={I_DOWNLOAD} className="w-[18px] h-[18px]" alt="" />
                                </div>
                                <div className="flex flex-col items-start">
                                    <span className="text-[13px] font-medium text-[#374151]">첨부파일</span>
                                    <span className="text-[12px] text-[#3B82F6]">{item?.fileName}</span>
                                </div>
                            </button>
                        )}
                    </div>
                ))}

            {/* 마커가 참조하지 않은 운동 (마커 인터리브 모드) */}
            {workoutRecord && hasWorkoutMarkers && unreferencedExercises.length > 0 && (
                <div className="flex flex-col gap-[10px] mb-[12px]">
                    {unreferencedExercises.map((exercise, index) => (
                        <WorkoutExerciseCard
                            key={`unref-${exercise.order}-${index}`}
                            exercise={exercise}
                            displayNumber={exercise.order}
                            onMediaClick={handleWorkoutMediaClick}
                        />
                    ))}
                </div>
            )}

            {/* 마지막 메시지 (마커 인터리브 모드: 본문 하단) */}
            {workoutRecord && hasWorkoutMarkers && workoutRecord.closingMessage && (
                <WorkoutClosingBlock text={workoutRecord.closingMessage} />
            )}

            {/* 운동 기록 번들 섹션 (마커 없을 때 — 현재 대부분) */}
            {workoutRecord && !hasWorkoutMarkers && (
                <WorkoutRecordView
                    workoutRecord={workoutRecord}
                    onMediaClick={handleWorkoutMediaClick}
                />
            )}
        </div>
    );
};

export default ModalFeedDetailBody;
